import { IMailData } from "../Types/IMailData";
import { IMailInstanceData } from "../Types/IMailInstanceData";
import { PlayerDataSingleton } from "./PlayerDataSingleton";
import { MailState } from "../Types/MailState";
import { MailType } from "../Types/MailType";
import { SAVE_KEYS } from "../Constants/SaveKeys";
import { IMedCaseJSON } from "../Types/IMedCaseJSON";
import { ISymDiagData } from "../Types/ISymDiagData";
import { HospitalIDMapper } from "../Data/HospitalIDMapper";
import { ProcessState } from "../Constants/ProcessState";
import { uppercaseFirstLettersInString } from "../../engine/HelperFunctions/uppercaseFirstLettersInString";
import { ITreatmentData } from "../Types/ITreatmentData";
import { DepartmentToString } from "../Types/Department";
import { formatTimestampToHHMM } from "../../engine/HelperFunctions/formatTimestampToHHMM";
import { GAME_DEBUG_MODE } from "../../engine/Constants/Constants";
import { formatTimestampAsWeekday } from "../../engine/HelperFunctions/formatTimestampAsWeekday";
import { getSeededRandomFromString } from "../../engine/HelperFunctions/getSeededRandomFromString";
import { Professions } from "../Data/Professions";
import { INCORRECT_EXAM_SCORE_PENALTY, INCORRECT_TRT_SCORE_PENALTY } from "../Constants/SharedConstants";
import { getAgeFromRand } from "./getAgeFromRand";
import { Windows95 } from "../States/Windows95";
import { HospitalNetCore } from "./HospitalNetCore";
import { GameModes } from "../Types/GameModes";

export enum CaseUpdateType {
    Exam = "exam",
    Treatment = "trtmnt",
    Diagnosis = "diag",
}

export class MailCore {

    private static mailContentsRef: IMailInstanceData[];
    private static mailData: Record<string, IMailData> = {};
    private static initialized: boolean = false;

    public static initialize(_mailData: Record<string, IMailData>): void {
        MailCore.mailData = Object.assign(MailCore.mailData, _mailData);
        MailCore.mailContentsRef = PlayerDataSingleton.getMailboxContents();
        MailCore.initialized = true;
    }

    /**
     * returns true if the email provided is a case file
     */
    public static isEmailACase(_emailId: string, _deprecated_emailType?: MailType): boolean {
        return _emailId.indexOf("CASE") === 0;
    }

    public static isEmailADiagnosisUpdate(_emailId: string): boolean {
        return _emailId.indexOf("DIAGNOSIS") === 0;
}

    public static isEmailACaseUpdate(_emailId: string, _emailType: MailType): boolean {
        return _emailType == MailType.CASE_UPDATE || _emailId.indexOf("UPDATE") === 0;
    }

    public static deleteMaildata( _emailId: string ): void {
        this.mailData[_emailId] = null;
    }

    public static deleteMailInstance( _id: string ): void {
        const resIndex = this.mailContentsRef.findIndex((e) => e.id == _id);
        if(resIndex !== -1) {
            this.mailContentsRef.splice(resIndex, 1);
        }
        PlayerDataSingleton.dirtify(SAVE_KEYS.MailboxContents);
    }

    public static deleteAllCaseUpdates(): void {
        const indicesToDelete: number[] = [];
        this.mailContentsRef.forEach((e, i) => {
            const splitStr = e.id.split("-");
            if(
                splitStr[0] === "UPDATE"
            ) {
                indicesToDelete.push(i);
            }
        });

        if(GAME_DEBUG_MODE) console.log("Deleting %i case update emails", indicesToDelete.length);

        let counter = 0;
        for(let i = 0; i < this.mailContentsRef.length; i++) {
            if(indicesToDelete.indexOf(i+counter) !== -1) {
                this.mailContentsRef.splice(i, 1);
                i--;
                counter++;
            }
        }
    }

    // {"dateReceived":1679078552808,"id":"UPDATE-Toni Griffin-diag-XZ8dHMxMyL7NiKCGAgzDaqKsPL0=-success","state":1,"type":2}
    public static deleteUpdatesFromCase( _case: IMedCaseJSON ): void {
        const indicesToDelete: number[] = [];
        this.mailContentsRef.forEach((e, i) => {
            const splitStr = e.id.split("-");
            if(
                splitStr[0] === "UPDATE" &&
                splitStr[1].toLowerCase() === _case.patientName.toLowerCase()
            ) {
                indicesToDelete.push(i);
            }
        });

        if(GAME_DEBUG_MODE) console.log("Deleting %i case update emails", indicesToDelete.length);

        let counter = 0;
        for(let i = 0; i < this.mailContentsRef.length; i++) {
            if(indicesToDelete.indexOf(i+counter) !== -1) {
                this.mailContentsRef.splice(i, 1);
                i--;
                counter++;
            }
        }
    }

    public static buildDiagnosisIdString(
        _caseId: string,
        _patientName: string,
        _id: string,
        _success: boolean,
        _breakdownJson: { docScore?: number, breakdown?: {
                difficultyBonus: number;
                examsXp: number;
                numOfUntreatedSymptoms: number;
                exams: number; // correct ones
                treatments: number; // correct ones
                treatmentsXp: number;
                speedBonus: number;
            } }
    ): string {
        // DIAGNOSIS-abcdef12345=-Doug Judy-ghijkl67890=-[1 for success, 0 for not]-[breakdownjson]
        const baseString = `DIAGNOSIS-${_caseId}-${_patientName}-${_id}-${Number(_success)}-${JSON.stringify(_breakdownJson)}`;
        // let result = `${baseString}`;
        return baseString;
    }

    public static buildUpdateIdString(
        _patientName: string,
        _type: CaseUpdateType,
        _id: string,
        _endTime?: number,
        _success?: boolean
    ): string {
        return `UPDATE-${_patientName}-${_type}-${_id}${_endTime ? `-${_endTime}` : (_success ? "-success" : "")}`;
    }

    public static createMaildataFromDiagnosis(
        _mail: IMailInstanceData,
        _caseData?: IMedCaseJSON
    ): IMailData {
        const splitStr = _mail.id.split("-");
        const _caseId: string = splitStr[1];
        if(!_caseData)
            _caseData = PlayerDataSingleton.getPastCases()[_caseId];
        const _patientName = uppercaseFirstLettersInString(splitStr[2]);
        const _diagnosisId = (splitStr[3]);
        const diagnosisStr = HospitalIDMapper.idToStringObject[_diagnosisId];
        const wasSuccessful = (splitStr[4] === "1");
        const breakdownJsonString = (splitStr.slice(5).join("-"));
        let breakdownJson: { score: number, breakdown: {
                difficultyBonus: number;
                examsXp: number;
                numOfUntreatedSymptoms: number;
                exams: number; // correct ones
                treatments: number; // correct ones
                treatmentsXp: number;
                speedBonus: number;
            } };
        try {
            breakdownJson = JSON.parse(breakdownJsonString);
        } catch(err) {
            console.error(err);
            breakdownJson = {
                score: NaN, breakdown: {
                    difficultyBonus: NaN,
                    numOfUntreatedSymptoms: NaN,
                    examsXp: NaN,
                    exams: NaN, // correct ones
                    treatments: NaN, // correct ones
                    treatmentsXp: NaN,
                    speedBonus: NaN,
                } };
        }

        const result: IMailData = {
            contents: `
Your diagnosis of <bold>${diagnosisStr}</bold> on patient <bold>${_patientName}</bold> was <bold>${wasSuccessful ? "correct" : "incorrect"}!</bold>

Your DoctOS XP has been updated and the case has been removed from your open cases${wasSuccessful ? "." : ", to be taken by another doctor."} 
The XP breakdown can be found below:

<bold>Patient Name: </bold> ${_patientName}
<bold>Final diagnosis: </bold> ${
    !wasSuccessful ? `<red>${diagnosisStr}</red>` : `<green>${diagnosisStr}</green>`
}${
(_caseData && typeof breakdownJson.breakdown.numOfUntreatedSymptoms !== "undefined") ? `\n<bold>Untreated symptoms:</bold> ${breakdownJson.breakdown.numOfUntreatedSymptoms}` : ``}

<bold>XP from examinations: </bold> ${
        breakdownJson.breakdown.examsXp <= 0 ? "<red>" : ""
    }${breakdownJson.breakdown.examsXp} XP ${
        breakdownJson.breakdown.examsXp <= 0 ? "</red>" : ""
}${
_caseData ? 
    `\n(You lost <red>${
    Math.abs(INCORRECT_EXAM_SCORE_PENALTY * (_caseData.attemptedExaminations.length - breakdownJson.breakdown.exams))
}</red> XP from incorrect exams!)`
    : ""
}

<bold>XP from treatments: </bold> ${
    breakdownJson.breakdown.treatmentsXp <= 0 ? "<red>" : ""                
}${breakdownJson.breakdown.treatmentsXp} XP ${
    breakdownJson.breakdown.treatmentsXp <= 0 ? "</red>" : ""
}${
    _caseData ?
        `\n(You lost <red>${
        Math.abs(INCORRECT_TRT_SCORE_PENALTY * (breakdownJson.breakdown.numOfUntreatedSymptoms))
    }</red> XP from untreated symptoms!)`
        : ""
}

<bold>Speed bonus: </bold> ${
    breakdownJson.breakdown.speedBonus > 0 ?
        `<green>${breakdownJson.breakdown.speedBonus} XP</green>` : ` XP`
}
<bold>Difficulty bonus: </bold> <green>${Math.round(breakdownJson.breakdown.difficultyBonus)} XP</green>
${PlayerDataSingleton.getGameMode() === GameModes.REALISTIC ? `<bold>Realism mode bonus: </bold> <green>${
    Math.round(breakdownJson.score / (1.33)) 
} XP</green>` : ""}

<bold>Final score: </bold> <green>${Math.round(breakdownJson.score * 100) / 100} XP</green>

`, // _mail.id,
            from: "DoctOS Case Update",
            subject: _patientName + " - Diagnosis made " + (wasSuccessful ? "successfully" : "unsuccessfully"),
            to: "You"
        };

        return result;
    }

    public static createMaildataFromCaseUpdate(_mail: IMailInstanceData): IMailData {
        const splitStr = _mail.id.split("-");
        const _patientName = uppercaseFirstLettersInString(splitStr[1]);
        const _type: CaseUpdateType = splitStr[2] as CaseUpdateType;
        let _typeStr = "";
        switch(_type) {
            case CaseUpdateType.Exam:
                _typeStr = "Examination";
                break;
            case CaseUpdateType.Treatment:
                _typeStr = "Treatment";
                break;
            case CaseUpdateType.Diagnosis:
                _typeStr = "Diagnosis";
                break;
        }
        const _id = splitStr[3];
        const idStr = HospitalIDMapper.idToStringObject[_id];
        const fourthParamExists = Boolean(splitStr[4]);
        const isRequest = fourthParamExists && !Number.isNaN(parseInt(splitStr[4]));

        let contentsStr: string = "";
        switch(_type) {
            case CaseUpdateType.Treatment:
                if(!isRequest) {
                    contentsStr = `
The treatment "${
                        idStr
                    }" on your patient "${
                        _patientName
                    }" has been administered. Check the case email for the results.`;
                    break;
                }
                // deliberate fallthrough here!
                // break;
// eslint-disable-next-line no-fallthrough
            case CaseUpdateType.Exam:
                if(!isRequest) {
                    contentsStr = `
The examination "${
                        idStr
                    }" on your patient "${
                        _patientName
                    }" has been completed. Check the case email for the results.`;
                } else {
                    contentsStr = `
We have received your request for a "${idStr}" ${_typeStr.toLowerCase()} on your patient ${_patientName}. Please check your case mail to check results when it is complete.`;
                }
                break;
//             case CaseUpdateType.Diagnosis:
//                 // contentsStr = `Diagnosis`;
//                 if(fourthParamExists && splitStr[4] === "success") {
//                     contentsStr = `
// The diagnosis of "${
//                         idStr
//                     }" on your patient "${
//                         _patientName
//                     }" was correct! The case mail has been removed from your inbox and your hospital stats have been updated. Congrats!`;
//                     break;
//                 } else {
//                     contentsStr = `
// The diagnosis of "${
//                         idStr
//                     }" on your patient "${
//                         _patientName
//                     }" was not correct! Your hospital stats have been updated.`;
//                 }
//                 break;
            default:
                console.warn("Unhandled mail type %s", _type);
                break;
        }

        const _completeOrSuccess = splitStr[4] || null;
        let endOfString = "";
        if(fourthParamExists && Number.isNaN(parseInt(splitStr[4]))) {
            // it exists so it should say "Exam completed"
            endOfString = "successfully completed";
        } else if(isRequest) {
            // it doesnt exist so it should say requested
            const timestamp = parseInt(splitStr[4]);

            const date = new Date(timestamp).getDate();
            const nowDate = new Date(PlayerDataSingleton.getGameTime()).getDate();
            if(date > nowDate) {
                endOfString = `requested (done by next ${formatTimestampAsWeekday(date)})`;
            } else if(date < nowDate) {
                endOfString = `requested (done last ${formatTimestampAsWeekday(date)})`;
            } else {
                endOfString = `requested (done by ${formatTimestampToHHMM(
                    timestamp
                )})`;
            }

        } else {
            endOfString = `requested`;
        }

        const result: IMailData = {
            contents: contentsStr,
            from: _type === CaseUpdateType.Diagnosis ? "DoctOS Case Server" : "DoctOS Case Update",
            subject: _type === CaseUpdateType.Diagnosis ?
                _patientName + " - Diagnosis made " + (_completeOrSuccess ? "successfully" : "unsuccessfully")
                :
                `${_patientName} - ${_typeStr} "${
                    idStr
                }" ${
                    endOfString
                }`,
            to: "You"
        };

        return result;
    }

    public static addCustomMail(
        _mailId: string,
        _mailData: IMailData
    ): void {
        MailCore.mailData[_mailId] = _mailData;
    }

    public static createMaildataFromCase(_case: IMedCaseJSON): IMailData {
        function createSymptomsListString(_symptoms: ISymDiagData[], _indent: number = 0) {
            let symptomsStr = "";
            const indentation = new Array(_indent).fill(" ").join("");
            let numberOfHiddenSymptoms = 0;
            _symptoms.forEach((e) => {
                if(e.state == 0) {
                    numberOfHiddenSymptoms++;
                    return;
                }

                const treatments = HospitalIDMapper.getString("symptomsToTreatments", e.id);
                let treatedStr = "";
                if(
                    e.state == ProcessState.SYM_TREATED_VISIBLE ||
                    e.state == ProcessState.SYM_TREATED
                ) {
                    treatedStr = "(treated)";
                } else {
                    const trt = _case.currentTreatments.find((e) => {
                        return treatments.indexOf(e.id) !== -1;
                    });
                    if(trt) {
                        treatedStr = "(treated)";
                    }
                }

                symptomsStr += `${indentation}  - <hyperlink url="symptoms/${
                    e.id
                }">${HospitalIDMapper.idToStringObject[e.id]}</hyperlink> ${
                    treatedStr
                }\n`;
            });
            // if(numberOfHiddenSymptoms) {
            symptomsStr += `${indentation}  - ${
                numberOfHiddenSymptoms > 0 ? "<red>" : "<green>"
            }${numberOfHiddenSymptoms} unidentified symptoms${
                numberOfHiddenSymptoms > 0 ? "</red>" : "</green>"
            }`;
            // }
            return symptomsStr;
        }
        function createTreatmentsString(_treatments: ITreatmentData[], _indent: number = 0) {
            let examsStr = "";
            const indentation = new Array(_indent).fill(" ").join("");
            if(_treatments.length == 0) {
                examsStr += `${indentation}  - n/a`;
            } else {
                _treatments.forEach((e) => {
                    let stateStr = "";
                    switch(e.state) {
                        case ProcessState.EXAM_IN_PROGRESS:
                            if(e.endTime < PlayerDataSingleton.getGameTime()) {
                                stateStr = "Done?";
                            } else {
                                const date = new Date(e.endTime);
                                if(date.getDate() !== new Date(PlayerDataSingleton.getGameTime()).getDate()) {
                                    stateStr = `requested, done by next ${formatTimestampAsWeekday(date)}`;
                                } else {
                                    stateStr = `done by ${formatTimestampToHHMM(
                                        e.endTime - (e.endTime % (60000)) + (60000)
                                    )}`;
                                }
                            }
                            break;
                        case ProcessState.EXAM_COMPLETE:
                        case ProcessState.EXAM_COMPLETE_ACK:
                            stateStr = "Done";
                            break;
                    }

                    examsStr += `${indentation}  - <hyperlink url="treatments/${
                        e.id
                    }">${HospitalIDMapper.idToStringObject[e.id]}</hyperlink> (${stateStr})\n`;
                });
            }
            return examsStr;
        }
        function createExamsString(_attemptedExaminations: ITreatmentData[], _indent: number = 0) {
            let examsStr = "";
            const indentation = new Array(_indent).fill(" ").join("");
            if(_attemptedExaminations.length == 0) {
                examsStr += `${indentation}  - n/a`;
            } else {
                _attemptedExaminations.forEach((e) => {
                    let stateStr = "";
                    switch(e.state) {
                        case ProcessState.EXAM_IN_PROGRESS:
                            if(e.endTime < PlayerDataSingleton.getGameTime()) {
                                stateStr = "Done?";
                            } else {
                                stateStr = `Done by: ${formatTimestampToHHMM(
                                    e.endTime - (e.endTime % (60000)) + (60000)
                                )}`;
                            }
                            break;
                        case ProcessState.EXAM_COMPLETE:
                        case ProcessState.EXAM_COMPLETE_ACK:
                            stateStr = "Done";
                            break;
                    }

                    examsStr += `${indentation}  - <hyperlink url="exams/${
                        e.id
                    }">${HospitalIDMapper.idToStringObject[e.id]}</hyperlink> (${stateStr})\n`;
                });
            }
            return examsStr;
        }

        const rand = getSeededRandomFromString(_case.patientName);
        const age =
            getAgeFromRand(rand);
        let profession: string = Professions[
            Math.min(
                Math.abs(
                    Math.floor(
                        Professions.length * rand
                    )
                ),
                Professions.length - 1
            )
        ];
        if(age >= 70 - Math.round(rand * 10)) {
            profession = "Retired, former " + profession;
        } else if(age >= 6 && age < (20 - Math.round(rand * 4))) {
            profession = "Student";
        }
        // else if(age < 6) {
        //     profession = "n/a";
        // }
        const tabs = "\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t";

        const estimatedRewardScore_success = HospitalNetCore.calculateRewardScore(
            _case,
            true
        );
        // const estimatedRewardScore_failure = HospitalNetCore.calculateRewardScore(
        //     _case,
        //     false
        // );

        return {
            contents: `
${tabs}<bold>Patient Name:</bold> ${uppercaseFirstLettersInString(_case.patientName)}
${tabs}<bold>Patient Age:</bold> ${age}
${tabs}<bold>Patient Profession:</bold> ${profession}
${tabs}<bold>Department:</bold> ${DepartmentToString[_case.department]}
${tabs}<bold>Can communicate:</bold> ${HospitalNetCore.canPatientSpeak(_case) ? "Yes" : "No"}

${tabs}<bold>Case Difficulty:</bold> ${"★".repeat(Math.round(_case.difficultyScore))}
${tabs}<bold>Current Estimated Reward:</bold> <green>${Math.round(estimatedRewardScore_success.score)} XP</green>
\n
    <bold>Visible symptoms:</bold>
${createSymptomsListString(_case.currentSymptoms, 5)}

    <bold>Examinations:</bold>
${createExamsString(_case.attemptedExaminations, 5)}

    <bold>Treatments:</bold>
${createTreatmentsString(_case.currentTreatments, 5)}


<hyperlink url="requestexam">Request examination</hyperlink>

<hyperlink url="requesttreatment">Prescribe treatment</hyperlink>

<hyperlink url="makediagnosis">Make diagnosis</hyperlink>



<hyperlink url="abandoncase" fill="0xff0000">Abandon case</hyperlink>






${
    __PRODUCTION ? "" : `<i>Case ID: ${_case.id}</i>`
}

`,
            from: "DoctOS Case Server",
            subject: `Active case - ${uppercaseFirstLettersInString(_case.patientName)}`,
            to: "You"
        };
    }

    public static markEmailAsRead(_id: string): void {
        const mail = MailCore.mailContentsRef.find((e) => e.id == _id);
        if(mail && mail.state == MailState.UNREAD) {
            mail.state = MailState.READ;
            PlayerDataSingleton.dirtify(SAVE_KEYS.MailboxContents);
        }
    }

    public static sendEmailToPlayer(
        _mailId: string,
        _mailType: MailType = MailType.INFO,
        _mailState: MailState = MailState.UNREAD,
        _receivedTime: number = PlayerDataSingleton.getGameTime(),
        _skipNotification: boolean = false
    ): IMailInstanceData {
        if(
            // _mailType == MailType.CASE &&
            this.mailContentsRef.findIndex((e) => e.id === _mailId) !== -1
        ) {
            console.warn("case email %s exists, ignoring", _mailId);
            return;
        }

        const content: IMailInstanceData = {
            dateReceived: _receivedTime,
            id: _mailId,
            state: _mailState,
            type: _mailType
        };
        if(_mailType === MailType.CASE) {
            this.mailContentsRef.unshift(
                content
            );
        } else {
            this.mailContentsRef.splice(
                this.mailContentsRef.findIndex((e) => e.type !== MailType.CASE),
                0,
                content
            );
        }
        PlayerDataSingleton.dirtify(SAVE_KEYS.MailboxContents);
        if(!_skipNotification) {
            try {
                const mailData = this.getDataFromMailInstance(content);
                (ENGINE.getActiveState() as Windows95)["_taskbar"]?.showNotification({
                    id: "MAIL_" + _mailId,
                    iconKey: "mail-icon",
                    titleStr: "You got mail!",
                    mainStr: `You received an email:\n${
                        mailData.subject
                    }`
                });
            } catch(err) {
                console.warn(err);
            }
        }
        return content;
    }

    public static debug_getMailDataFromId(_id: string): IMailData {
        return this.mailData[_id];
    }

    public static getDataFromMailInstance(_instance: IMailInstanceData): IMailData {
        if(MailCore.isEmailACase(_instance.id, _instance.type)) {
            return this.mailData[_instance.id];
        } else if(MailCore.isEmailADiagnosisUpdate(_instance.id)) {
            return MailCore.createMaildataFromDiagnosis(_instance);
        } else if(MailCore.isEmailACaseUpdate(_instance.id, _instance.type)) {
            return MailCore.createMaildataFromCaseUpdate(_instance);
        } else {
            return this.mailData[_instance.id];
        }
    }
}